import { FilePublic } from '@repo/types/files';
import { ArrowLeft, ArrowRight, Download, LoaderCircle, Maximize2Icon, Minimize2Icon, Minus, Plus, Scaling } from 'lucide-react';
import { createContext, Dispatch, lazy, ReactNode, RefObject, SetStateAction, Suspense, use, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link } from 'react-router';

import type { MediaViewerFile } from '@/components/features/media/MediaViewer';

import { Button, ButtonGroup } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FullscreenRoot, FullscreenTarget, FullscreenTrigger, useFullscreen } from '@/components/ui/fullscreen';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getFileUrl } from '@/utils/file/getFileUrl';

const PDFViewerContent = lazy(() => import('@/components/features/media/PdfViewer'));

const MAX_PDF_PAGE_WIDTH = 800;

const PDFViewerLoader = () => (
  <div className={'flex h-full flex-col items-center justify-center py-20'}>
    <LoaderCircle className={'text-text-tertiary size-6 animate-spin'} aria-hidden={'true'} />
    <p className={'text-text-tertiary italic'}>Ładowanie...</p>
  </div>
);

interface PDFViewerErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
  canDownload: boolean;
  fileName: string;
  fileURL: string;
}

function PDFViewerErrorFallback(pdfViewerErrorFallbackProps: PDFViewerErrorFallbackProps) {
  const { onRetry, canDownload, fileName, fileURL } = pdfViewerErrorFallbackProps;
  return (
    <div className={'flex h-full flex-col items-center justify-center gap-5 py-16 text-center'}>
      <div className={'flex max-w-md flex-col gap-1'}>
        <p className={'text-lg font-semibold'}>Nie udało się załadować dokumentu</p>
        <p className={'text-text-tertiary text-sm'}>Spróbuj ponownie lub pobierz plik, aby otworzyć go w nowej karcie.</p>
      </div>
      <div className={'flex flex-wrap items-center justify-center gap-3'}>
        <Button variant={'secondary'} onClick={onRetry}>
          Spróbuj ponownie
        </Button>
        {canDownload && (
          <Button variant={'outline'} asChild>
            <Link to={fileURL} download={fileName} target={'_blank'} rel={'noreferrer'}>
              <Download />
              Pobierz plik
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

const PDFViewerErrorTrigger = ({ error }: { error: Error }) => {
  throw error;
};

// Context types
interface MediaPdfViewerContextValue {
  fileURL: string;
  isFilesInstance: boolean;
  numPages: number | null;
  currentPage: number;
  scale: number;
  setScale: Dispatch<SetStateAction<number>>;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
  pageRefs: RefObject<{ [key: number]: HTMLDivElement | null }>;
  viewerError: Error | null;
  setViewerError: Dispatch<SetStateAction<Error | null>>;
  viewerRetryKey: number;
  setNumPages: Dispatch<SetStateAction<number | null>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setRenderedPages: Dispatch<SetStateAction<number[]>>;
  setLoadingMorePages: Dispatch<SetStateAction<boolean>>;
  renderedPages: number[];
  searchText: string;
  getPageWidth: () => number;
  incrementRetryKey: () => void;
  file: FilePublic | File;
}

const MediaPdfViewerContext = createContext<MediaPdfViewerContextValue | null>(null);

interface MediaPdfViewerProviderProps {
  file: FilePublic | File;
  children: ReactNode;
}

// Provider component
export function MediaPdfViewerProvider(mediaPdfViewerProviderProps: MediaPdfViewerProviderProps) {
  const { file, children } = mediaPdfViewerProviderProps;
  const isFilesInstance = 'id' in file;

  const fileURL = useMemo<string>(() => {
    if (isFilesInstance) {
      return getFileUrl(file.id);
    }
    return URL.createObjectURL(file);
  }, [isFilesInstance, file]);

  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [renderedPages, setRenderedPages] = useState<number[]>([1]);
  const [scale, setScale] = useState<number>(1.0);
  const [loadingMorePages, setLoadingMorePages] = useState<boolean>(false);
  const [viewerError, setViewerError] = useState<Error | null>(null);
  const [viewerRetryKey, setViewerRetryKey] = useState<number>(0);
  const [searchText] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Reset when file changes
  useEffect(() => {
    setNumPages(null);
    setCurrentPage(1);
    setRenderedPages([1]);
    setLoadingMorePages(false);
    setViewerError(null);
    setViewerRetryKey(0);
  }, [fileURL]);

  const ensurePageIsRendered = (pageNumber: number) => {
    if (!renderedPages.includes(pageNumber)) {
      setRenderedPages((prev) => [...prev, pageNumber].sort((a, b) => a - b));
    }
  };

  const goToPage = (pageNumber: number) => {
    ensurePageIsRendered(pageNumber);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        pageRefs.current[pageNumber]?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      });
    });
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (numPages && currentPage < numPages) {
      goToPage(currentPage + 1);
    }
  };

  const getPageWidth = () => {
    if (!containerRef.current) return MAX_PDF_PAGE_WIDTH;
    return Math.min(MAX_PDF_PAGE_WIDTH, containerRef.current.clientWidth - 12 * 2);
  };

  // Scroll / infinite‑load logic
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!containerRef.current || !numPages || loadingMorePages) return;

      const { scrollTop, clientHeight, scrollHeight } = containerRef.current;
      const containerRect = containerRef.current.getBoundingClientRect();

      let mostVisiblePage: number | null = null;
      let maxVisibility = 0;

      Object.entries(pageRefs.current).forEach(([pageNumberStr, ref]) => {
        if (!ref) return;
        const pageNumber = Number(pageNumberStr);
        const rect = ref.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top);

        if (visibleHeight > maxVisibility) {
          maxVisibility = visibleHeight;
          mostVisiblePage = pageNumber;
        }
      });

      if (mostVisiblePage !== null && mostVisiblePage !== currentPage) {
        setCurrentPage(mostVisiblePage);
      }

      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        const maxRendered = Math.max(...renderedPages);
        if (maxRendered < numPages) {
          setLoadingMorePages(true);
          const newPages = Array.from({ length: Math.min(3, numPages - maxRendered) }, (_, i) => maxRendered + 1 + i);
          setRenderedPages((prev) => [...prev, ...newPages]);
        }
        setTimeout(() => setLoadingMorePages(false), 500);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [numPages, loadingMorePages, currentPage, renderedPages]);

  const incrementRetryKey = () => setViewerRetryKey((key) => key + 1);

  const value: MediaPdfViewerContextValue = {
    fileURL,
    isFilesInstance,
    numPages,
    currentPage,
    scale,
    setScale,
    goToPrevPage,
    goToNextPage,
    containerRef,
    pageRefs,
    viewerError,
    setViewerError,
    viewerRetryKey,
    setNumPages,
    setCurrentPage,
    setRenderedPages,
    setLoadingMorePages,
    renderedPages,
    searchText,
    getPageWidth,
    incrementRetryKey,
    file,
  };

  return <MediaPdfViewerContext value={value}>{children}</MediaPdfViewerContext>;
}

export function useMediaPdfViewerContext() {
  const context = use(MediaPdfViewerContext);
  if (!context) {
    throw new Error('useMediaPdfViewerContext must be used within a MediaPdfViewerProvider');
  }
  return context;
}

export function MediaPDFViewerContent() {
  const {
    fileURL,
    isFilesInstance,
    numPages,
    scale,
    containerRef,
    pageRefs,
    viewerError,
    viewerRetryKey,
    setViewerError,
    setNumPages,
    setCurrentPage,
    setRenderedPages,
    setLoadingMorePages,
    incrementRetryKey,
    renderedPages,
    getPageWidth,
    searchText,
    file,
  } = useMediaPdfViewerContext();

  return (
    <div ref={containerRef} className={'grow overflow-auto p-3'}>
      <ErrorBoundary
        resetKeys={[viewerRetryKey, fileURL]}
        onReset={() => setViewerError(null)}
        fallbackRender={({ error, resetErrorBoundary }) => (
          <PDFViewerErrorFallback
            error={error ?? viewerError}
            onRetry={() => {
              setViewerError(null);
              setNumPages(null);
              setCurrentPage(1);
              setRenderedPages([1]);
              setLoadingMorePages(false);
              incrementRetryKey();
              resetErrorBoundary();
            }}
            canDownload={isFilesInstance}
            fileName={file.name}
            fileURL={fileURL}
          />
        )}
      >
        <Suspense key={viewerRetryKey} fallback={<PDFViewerLoader />}>
          {viewerError ? (
            <PDFViewerErrorTrigger error={viewerError} />
          ) : (
            <PDFViewerContent
              fileURL={fileURL}
              scale={scale}
              searchText={searchText}
              renderedPages={renderedPages}
              pageRefs={pageRefs}
              getPageWidth={getPageWidth}
              onDocumentLoadSuccess={({ numPages: nextNumPages }) => {
                setViewerError(null);
                setNumPages(nextNumPages);
                setRenderedPages(Array.from({ length: Math.min(3, nextNumPages) }, (_, i) => i + 1));
              }}
              onDocumentLoadError={(error: Error) => {
                setViewerError(error);
                setLoadingMorePages(false);
              }}
              numPages={numPages}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default function MediaPDFViewer(mediaPDFViewerProps: MediaViewerFile) {
  const { file, open, onOpenChange } = mediaPDFViewerProps;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MediaPdfViewerProvider file={file}>
        <FullscreenRoot>
          <FullscreenTarget asChild fullscreenClassName="fixed inset-0 w-screen h-screen max-w-none max-h-none rounded-none shadow-none top-0 left-0 translate-x-0 translate-y-0">
            <DialogContent className={'h-full md:max-w-196'}>
              <DialogBody>
                <DialogHeader className={'border-border-primary flex-col items-start gap-4 border-b'}>
                  <DialogTitle>{file.name}</DialogTitle>
                  <div className="flex w-full justify-between">
                    <MediaPDFViewerNavigation />
                    <MediaPDFViewerNavigationResize />
                  </div>
                </DialogHeader>
                <MediaPDFViewerContent />
              </DialogBody>

              <DialogFooter className={'border-border-primary space-x-0.5 border-t'}>
                <FullscreenTrigger asChild>
                  {(isFullscreen) => (
                    <Button variant="ghost" size="sm">
                      {isFullscreen ? <Minimize2Icon /> : <Maximize2Icon />}
                    </Button>
                  )}
                </FullscreenTrigger>
                <MediaPDFViewerDownloadButton />
              </DialogFooter>
            </DialogContent>
          </FullscreenTarget>
        </FullscreenRoot>
      </MediaPdfViewerProvider>
    </Dialog>
  );
}

export function MediaPDFViewerDownloadButton() {
  const { fileURL, isFilesInstance, file } = useMediaPdfViewerContext();
  if (!isFilesInstance) return null;
  return (
    <Button variant={'ghost'} size="sm" asChild>
      <Link to={fileURL} download={file.name} target="_blank" rel="noreferrer">
        <Download />
      </Link>
    </Button>
  );
}

export function MediaPDFViewerNavigation() {
  const { goToPrevPage, goToNextPage, currentPage, numPages } = useMediaPdfViewerContext();
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={goToPrevPage} disabled={currentPage <= 1}>
        <ArrowLeft />
      </Button>
      <span className={'text-text-secondary w-14 text-center text-sm'}>
        {currentPage} / {numPages ?? '...'}
      </span>
      <Button variant="outline" size="sm" onClick={goToNextPage} disabled={!numPages || currentPage >= numPages}>
        <ArrowRight />
      </Button>
    </div>
  );
}

export function MediaPDFViewerNavigationResize() {
  const { scale, setScale } = useMediaPdfViewerContext();
  const { isFullscreen, toggle } = useFullscreen();

  return (
    <ButtonGroup>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.max(0.5, s - 0.1))} disabled={scale <= 0.5}>
            <Minus />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Pomniejsz</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" onClick={() => setScale((s) => s + 0.1)}>
            <Plus />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Powiększ</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" disabled={scale === 1.0} onClick={() => setScale(1.0)}>
            <Scaling />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Resetuj skalowanie</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" onClick={toggle} aria-label={isFullscreen ? 'Wyjdź z pełnego ekranu' : 'Pełny ekran'}>
            {isFullscreen ? <Minimize2Icon /> : <Maximize2Icon />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isFullscreen ? 'Wyjdź z pełnego ekranu' : 'Pełny ekran'}</TooltipContent>
      </Tooltip>
    </ButtonGroup>
  );
}
